import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { GitBranch, X } from 'lucide-react';
import { BranchNodeData, BRANCH_HANDLE_TRUE, BRANCH_HANDLE_FALSE } from '../types';

export function BranchNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as unknown as BranchNodeData;
  const [isHovered, setIsHovered] = useState(false);
  const { setNodes, setEdges } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
    setEdges((edges) => edges.filter((e) => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={`relative min-w-[200px] rounded-xl border-2 bg-white shadow-lg transition-all ${
        selected ? 'border-yellow-500 shadow-yellow-200' : 'border-yellow-400'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Delete button on hover */}
      {isHovered && (
        <button
          onClick={handleDelete}
          className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
          title="Delete node"
        >
          <X size={12} />
        </button>
      )}
      <Handle type="target" position={Position.Top} className="!bg-yellow-500 !w-3 !h-3" />
      <div className="flex items-center gap-2 rounded-t-xl bg-yellow-500 px-3 py-2">
        <GitBranch size={14} className="text-white" />
        <span className="text-xs font-semibold uppercase tracking-wider text-white">If / Branch</span>
      </div>
      <div className="px-3 py-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">Condition</p>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {data.displayLabel || `${data.field} ${data.operator} ${data.value}`}
        </p>
      </div>
      {/* Dual output handles with labels */}
      <div className="relative flex justify-between px-4 py-2 border-t border-yellow-200 bg-yellow-50 rounded-b-xl">
        {/* Yes/True handle - left side */}
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold text-green-600 mb-1">Yes</span>
          <Handle
            type="source"
            position={Position.Bottom}
            id={BRANCH_HANDLE_TRUE}
            className="!bg-green-500 !w-3 !h-3 !relative !transform-none !left-0 !right-auto !bottom-0"
            style={{ position: 'relative' }}
          />
        </div>

        {/* No/Else handle - right side */}
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold text-red-600 mb-1">No</span>
          <Handle
            type="source"
            position={Position.Bottom}
            id={BRANCH_HANDLE_FALSE}
            className="!bg-red-500 !w-3 !h-3 !relative !transform-none !left-0 !right-auto !bottom-0"
            style={{ position: 'relative' }}
          />
        </div>
      </div>
    </div>
  );
}
